import type { PowerPerformanceForms } from '../../../../types/mutual-fund-detail.types'
import type { FundSummaryMetrics } from '../../types/recommendation.types'
import { RecommendationVariant } from '../../types/recommendation.types'
import { roundOffAndFormatNumberWithCurrency } from '../../../../utils/utils'
import { FundSummaryCard } from './FundSummaryCard'
import { getMutualFundDetailCTA } from '../../../../utils/mutualFundDetail'
import { postCTA } from '../../../../utils/appBridge'
import {
  TaxHarvestingScreenName,
  TaxHarvestingEventId,
  trackTaxHarvestingUserClicked
} from '../../tracking/taxHarvestingTracking'

type RecommendationPageHeaderProps = {
  isin: string
  variant: RecommendationVariant
  headlineAmount: number
  saveTax: number
  isFutureTax: boolean
  fundLogo: string
  fundTitle: string
  fundNature: string
  fundCategory: string
  fundAmount: number
  fundSummaryMetrics: FundSummaryMetrics
  powerRank: number
  powerRating?: `${PowerPerformanceForms}`
  isRegular: boolean
}

export function RecommendationPageHeader({
  isin,
  variant,
  headlineAmount,
  saveTax,
  isFutureTax,
  fundLogo,
  fundTitle,
  fundNature,
  fundCategory,
  fundAmount,
  powerRank,
  powerRating,
  isRegular,
  fundSummaryMetrics
}: RecommendationPageHeaderProps) {
  const amountStr = roundOffAndFormatNumberWithCurrency(Math.abs(headlineAmount))
  const taxLabel = isFutureTax ? 'future tax' : 'tax'
  let title = ''
  if (variant === RecommendationVariant.GAINS_TAX_FREE) {
    title = saveTax > 0
      ? `Save ${roundOffAndFormatNumberWithCurrency(saveTax)} in ${taxLabel} by booking ${amountStr} gains from this fund`
      : `You can book ${amountStr} gains tax-free from this fund`
  } else {
    title = saveTax > 0
      ? `Save ${roundOffAndFormatNumberWithCurrency(saveTax)} in ${taxLabel} by booking ${amountStr} losses from this fund`
      : `You can book ${amountStr} losses to save ${taxLabel}`
  }

  const showFund = fundTitle !== ''

  const handleFundClick = (isin: string) => {
    if (isin) {
      trackTaxHarvestingUserClicked({
        screen: TaxHarvestingScreenName.Recommendation,
        eventId: TaxHarvestingEventId.FundSummaryCard,
        fundIsin: isin
      })
      postCTA(JSON.stringify(getMutualFundDetailCTA({ isin, navigationStyle: 'PUSH' })))
    }
  }

  return (
    <header className="flex flex-col w-full pb-6 pl-[19px] pr-5 border-b-[12px] border-[#F0F4F7]">
      {title ? (
        <h1 className="text-[20px] font-[500] text-[#232323] pr-8 leading-[130%]">
          {title}
        </h1>
      ) : null}
      {showFund && (
        <div onClick={() => handleFundClick(isin)} className="pt-4 pr-0">
          <FundSummaryCard
            fundLogo={fundLogo}
            fundTitle={fundTitle}
            fundNature={fundNature}
            fundCategory={fundCategory}
            amount={fundAmount}
            metrics={fundSummaryMetrics}
            powerRank={powerRank}
            powerRating={powerRating}
            isRegular={isRegular}
          />
        </div>
      )}
    </header>
  )
}
